import { BaseWebComponentController, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { EasmAssetsInventoryGridView } from '../view/EasmAssetsInventoryGridView';
import { EasmAssetsInventoryGridWebComponent } from '../component/EasmAssetsInventoryGridWebComponent';
import { EasmAssetsInventoryRepository } from '../repository/EasmAssetsInventoryRepository';
import { EasmAssetsInventoryGridViewData } from '../types';
import {
  Categories,
  SmartViewItemConfiguration,
  SmartViewsCreateRequest,
  SmartViewsUpdateRequest,
  SmartViewType,
} from '../../../grid/genericGridSmartViews/types';
import { openScanConfigurationModal } from '../../utils';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';
import { FindingInfo } from '../../easmFindingDetails/types';
import { FAVORITE_ICON_STATE } from '../../../grid/utils/constants';
import { ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { Column, ColumnKeys } from '../../../xdr/integrationsGrid/types';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { EasmArtifactsInventoryGridWebComponent } from '../../artifactsInventoryGrid';
import {
  EasmArtifactsInventoryRepository,
} from '../../artifactsInventoryGrid/repository/EasmArtifactsInventoryRepository';
import { EasmArtifactsInventoryGridView } from '../../artifactsInventoryGrid/view/EasmArtifactsInventoryGridView';
import { EasmArtifactsInventoryGridViewData } from '../../artifactsInventoryGrid/types';
import { GzButtonState } from '../../../ui/gzButton/types';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { Events, FindingClassType } from '../../types';
import { EasmAssetsInventoryGridDataProvider } from '../dataProvider/EasmAssetsInventoryGridDataProvider';
import {
  EasmArtifactsInventoryGridDataProvider,
} from '../../artifactsInventoryGrid/dataProvider/EasmArtifactsInventoryGridDataProvider';
import {
  EasmBulkChangePropertyWebComponent,
} from '../../bulkChangeProperty/component/EasmBulkChangePropertyWebComponent';
import { Property } from '../../bulkChangeProperty/types';

export class EasmInventoryBaseGridController extends BaseWebComponentController {
  public component: EasmAssetsInventoryGridWebComponent | EasmArtifactsInventoryGridWebComponent;
  public dataRepository: EasmAssetsInventoryRepository | EasmArtifactsInventoryRepository;
  public view: EasmAssetsInventoryGridView | EasmArtifactsInventoryGridView;
  protected selectedItemIds: string[] = [];

  public getCompanyIdFilterValue(): string {
    return this.view.getCompanyIdFilterValue();
  }

  public async getDataForView(): Promise<EasmArtifactsInventoryGridViewData | EasmAssetsInventoryGridViewData> {
    return this.dataRepository.getDataForView();
  }

  protected async onDataLoadingFinishedWithSuccess(): Promise<void> {
    this.view.removeElementClass(this.view.dom.$innerGridWrapper, 'hidden');
  }

  protected showSuccessToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.SUCCESS,
    });
  }

  protected showErrorToast(message: string): void {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: message,
      severity: ToastSeverity.ERROR,
    });
  }

  /**
   * defaultViewProps -> when clicking the reset grid filters this is the view that the grid will reset to
   * activeViewProps -> current active grid configuration( tracks changes)
   * activeInitialViewProps -> initial active grid configuration
   * @param {WebComponentEvent} event
   */
  protected async updateViewProps(event: WebComponentEvent): Promise<void> {
    await this.view.dom.$smartViews.updateSmartView(this.processSmartViewForUpdate(event.detail.viewConfiguration));
    this.view.dom.$gridView.activeViewProps = event.detail.viewConfiguration;
    this.view.dom.$gridView.activeViewInitialProps = event.detail.viewConfiguration;
    this.view.dom.$gridView.defaultViewProps = event.detail.viewConfiguration;
  }

  protected processSmartViewForUpdate(viewConfig: ViewConfiguration<Column, ColumnKeys>): SmartViewsUpdateRequest {
    return {
      id: viewConfig.id,
      details: {
        specifics: {
          filters: viewConfig.filters,
          columnConfigs: viewConfig.columnConfigs,
          displayedColumns: viewConfig.displayedColumns,
          pageSize: viewConfig.pageSize,
          compactView: viewConfig.compactView,
        },
      },
    };
  }

  protected async showSaveAsDialog(): Promise<void> {
    const viewConfiguration = {
      filters: this.view.dom.$gridView.activeViewProps.filters,
      displayedColumns: this.view.dom.$gridView.activeViewProps.displayedColumns,
      compactView: this.view.dom.$gridView.activeViewProps.compactView,
      columnConfigs: this.view.dom.$gridView.activeViewProps.columnConfigs,
      pageSize: this.view.dom.$gridView.activeViewProps.pageSize,
    };
    const { smartViewsStrings } = this.view.getTranslations();
    const viewCreationConfig = {
      dialogText: smartViewsStrings.saveGridViewFormDialog.dialogText,
    };
    this.view.dom.$smartViews.saveSmartView(
      this.convertGridConfigToGenericConfig(viewConfiguration),
      viewCreationConfig
    );
  }

  protected convertGridConfigToGenericConfig(viewConfig: any): SmartViewsCreateRequest {
    return {
      category: Categories.SAVED,
      type: SmartViewType.GRID_VIEW,
      specifics: {
        filters: viewConfig.filters,
        columnConfigs: viewConfig.columnConfigs,
        displayedColumns: viewConfig.displayedColumns,
        pageSize: viewConfig.pageSize,
        compactView: viewConfig.compactView,
      },
    };
  }

  protected async onSmartViewSelect(event: WebComponentEvent): Promise<void> {
    const smartView = event.detail.smartViewConfiguration as SmartViewItemConfiguration;

    if (smartView._id === this.view.dom.$gridView.viewId) {
      return;
    }

    this.view.dom.$detailsPanel.closeSideMenu();
    await this.view.dom.$smartViews.controller?.updateActiveViewId(smartView._id);
    await this.view.buildGridView(smartView);
  }

  protected openScanConfigurationWithResults(): void {
    const dependencies = this.dependencies;
    openScanConfigurationModal(this.getCompanyIdFilterValue(), dependencies);
  }

  protected handleRowClick(event: WebComponentEvent): void {
    if (event.detail.rowDetails) {
      this.openSideMenu({
        itemId: event.detail.rowDetails._id,
        itemType: event.detail.rowDetails.type,
        itemClass: event.detail.rowDetails.class,
      });
    }
  }

  protected showNewAssetSidePanel(event: WebComponentEvent): void {
    this.openSideMenu(event.detail);
  }

  protected openSideMenu(details: FindingInfo): void {
    this.view.dom.$detailsPanel.setDetailsInContent(details.itemId);
  }

  protected handleSmartViewFavoriteChanged(): void {
    const smartViewId = this.view.dom.$gridView.viewId;
    const currentSmartView = this.view.dom.$gridView.activeViewProps;
    if (currentSmartView.type === ViewType.DEFAULT) {
      this.showErrorToast(translateFn('Unable to set default smart view as favorite.'));
      return;
    }
    let newIsFavoriteFlag = false;
    let iconConfig = {
      disabled: false,
      icon: FAVORITE_ICON_STATE.OUTLINE,
      active: false,
    };
    if (!currentSmartView.isFavorite) {
      this.view.dom.$smartViews.makeSmartViewFavorite(smartViewId);
      iconConfig = {
        disabled: false,
        icon: FAVORITE_ICON_STATE.FILLED,
        active: true,
      };
      newIsFavoriteFlag = true;
    } else {
      this.view.dom.$smartViews.removeFromFavorite(smartViewId);
    }
    this.view.dom.$gridView.controller?.updateFavoriteIconConfig(iconConfig);
    //updating all grid related view props as the current view has changed
    this.view.dom.$gridView.activeViewProps.isFavorite = newIsFavoriteFlag;
    if (this.view.dom.$gridView.defaultViewProps) {
      this.view.dom.$gridView.defaultViewProps.isFavorite = newIsFavoriteFlag;
    }
    this.view.dom.$gridView.activeViewInitialProps.isFavorite = newIsFavoriteFlag;
  }

  protected handleCompanyFilterUpdated(event: WebComponentEvent): void {
    if (event.detail.activeView.category === Categories.DEFAULT) {
      this.dependencies.setActiveCompany(event.detail.selectedValue, event.detail.label);
    }
  }

  protected async onSmartViewCreated(event: WebComponentEvent): Promise<void> {
    const smartView = event.detail.createdView as SmartViewItemConfiguration;
    this.view.dom.$gridView.titleConfig = new GridTitleConfig(smartView.label);
  }

  protected complexEnumFilterColumns(): string[] {
    return [];
  }

  protected findingClassType(): FindingClassType {
    return FindingClassType.ASSET;
  }

  protected async handleCSVExportEvent(event: CustomEvent): Promise<void> {
    this.view.dom.$gridView.controller?.updateCsvButtonState(
      GzButtonState.IS_LOADING,
      this.dependencies.translateFn('Change the current filter selection to generate a new export.'),
    );

    try {
      let filters: any[] = convertGridFiltersToExtJsFilters(event.detail.filters);
      const dataForView = await this.getDataForView() as EasmAssetsInventoryGridViewData;

      const complexEnumFilterColumns = this.complexEnumFilterColumns();
      const remappedColumns = event.detail.columns
                                   .filter(columnKey => dataForView.allColumns.some(col => col.column === columnKey))
                                   .map(columnKey => {
                                     const column = dataForView.allColumns.find(col => col.column === columnKey);
                                     return column?.key;
                                   })
                                   .filter(Boolean);

      filters = this.handleComplexEnumFilter(complexEnumFilterColumns, filters);
      filters.push({
        property: 'class',
        value: this.findingClassType(),
      });

      const url = app.buildDownloadUrl('EASMFindings', 'exportItemsAsCsv', null, 'CSV');
      const smartView = this.view.dom.$smartViews.getActiveView();

      await Application.lib.DownloadManager.download(url, {
        filters,
        columns: remappedColumns,
        smartViewId: smartView?._id,
        classType: this.findingClassType(),
      }, true);
    } catch (error) {
      this.view.dom.$gridView.controller?.updateCsvButtonState(GzButtonState.ENABLED);
      this.showErrorToast(this.dependencies.translateFn('Failed to export'));
    }
  }

  protected handleBulkChangePropertyButtonClick(property: Property, event: CustomEvent): void {
    this.view.dom.$gridView.dispatchEvent(
      new CustomEvent(
        Events.CHANGE_PROPERTY,
        {
          detail: {
            property,
            findingsClassType: this.findingClassType(),
            findingIds: this.selectedItemIds,
            preSelectedValue: event.detail.id,
            preSelectedValueLabel: event.detail.optionLabel,
          },
        }
      )
    );
  }

  protected handleComplexEnumFilter(complexEnumFilterColumns: string[], filters: string[]): string[] {
    const filtersToRemove: Array<string> = [];
    for (const filter of filters) {
      if (complexEnumFilterColumns.includes(filter['property']) && filter['value']) {
        if (filter['value'].length === 0) {
          filtersToRemove.push(filter['property']);
        } else {
          filter['value'] = filter['value'].map(item => {
            return item.value;
          });
        }
      }
    }

    return filters.filter(filter => !filtersToRemove.includes(filter['property']));
  }

  protected openDetailsPanelIfDemanded(): void {
    const findings = (this.view.dom.$gridView.dataProvider as EasmAssetsInventoryGridDataProvider | EasmArtifactsInventoryGridDataProvider).getResponse()?.data ?? [];

    if (this.view.openDetailsPanel && 1 === findings.length) {
      this.openSideMenu({
        itemId: findings[0]._id,
        itemType: findings[0].type as unknown as string,
        itemClass: findings[0].class as FindingClassType,
      });
    }
  }

  protected handleChangeProperty(event: CustomEvent): void {
    const bulkChangePropertyComponent = new EasmBulkChangePropertyWebComponent(
      event.detail.property,
      event.detail.findingsClassType,
      event.detail.findingIds,
      event.detail.preSelectedValue ?? null,
      event.detail.preSelectedValueLabel ?? null,
      event.detail.options ?? null,
      event.detail.selectedOptionValue ?? null
    );

    bulkChangePropertyComponent.render();
  }

  protected async refreshGrid(resetSelectionStore: boolean): Promise<void> {
    await this.view.dom.$gridView.controller?.handleGridItemsLoad(resetSelectionStore, resetSelectionStore);
  }
}
